// プロジェクト管理 — shared constants, badge styles, helpers.
// EDITABLE workflow labels only. 'completed' was REMOVED: `status` no longer
// takes part in any lifecycle decision (services/project_lifecycle.py — finished
// iff planner_projects.archived), so offering 完了 here was a write that looked
// like it finished a project and silently did nothing anywhere. 完了 is the
// 完了/復元 action on the project screen, which flips `archived`.
export const PROJECT_STATUSES = ['active', 'on_hold'];
// Filter chips are NOT the same list: 'finished' is derived from `is_finished`,
// the canonical stored field, not from `status`.
export const PROJECT_FILTERS = ['active', 'on_hold', 'finished'];

// THE badge key for a project. Every surface that renders a project's state
// badge must go through this — see the guard test
// `test_no_frontend_surface_reads_status_for_the_badge`.
//
// Two rules, and the second is the one the defect turned on:
//   1. 完了 comes from `is_finished` (services/project_lifecycle.py), never
//      from `status`.
//   2. A legacy `status` of 'completed' / 'done' is CLAMPED to 'active'. It is
//      not a lifecycle fact and never was — nothing in the app writes it — so
//      rendering it made the project-detail screen say 完了 in the badge while
//      the 完了 button right next to it offered to finish the same project.
export const projectBadgeKey = (p) => {
  if (p?.is_finished === true) return 'completed';
  const s = p?.status;
  return PROJECT_STATUSES.includes(s) ? s : 'active';
};
export const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'done'];
export const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

export const ACCENT = '#4F46E5'; // indigo-600

export const PRIORITY_BADGE = {
  low: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  medium: 'bg-sky-50 text-sky-700 border-sky-200',
  high: 'bg-amber-50 text-amber-700 border-amber-200',
  urgent: 'bg-rose-50 text-rose-700 border-rose-200',
};
export const PRIORITY_BAR = {
  low: '#a1a1aa', medium: '#0ea5e9', high: '#f59e0b', urgent: '#f43f5e',
};
export const TASK_STATUS_BADGE = {
  todo: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  in_progress: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  blocked: 'bg-rose-50 text-rose-700 border-rose-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
export const TASK_STATUS_DOT = {
  todo: 'bg-zinc-400', in_progress: 'bg-indigo-500', blocked: 'bg-rose-500', done: 'bg-emerald-500',
};
export const PROJECT_STATUS_BADGE = {
  active: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export const PRIO_RANK = { urgent: 0, high: 1, medium: 2, low: 3 };

// JST の今日 (YYYY-MM-DD)
export const jstTodayStr = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' });

// 期限超過判定（完了タスクは対象外）
export const isOverdueTask = (tk, todayStr = jstTodayStr()) =>
  !!tk.due_date && tk.status !== 'done' && String(tk.due_date).slice(0, 10) < todayStr;

// カンバン並び順: 期限超過 → 期限昇順（未設定は最後）→ 優先度 → 作成日 → タイトル
export const kanbanOrder = (todayStr = jstTodayStr()) => (a, b) => {
  const ao = isOverdueTask(a, todayStr);
  const bo = isOverdueTask(b, todayStr);
  if (ao !== bo) return ao ? -1 : 1;
  const ad = a.due_date ? String(a.due_date).slice(0, 10) : null;
  const bd = b.due_date ? String(b.due_date).slice(0, 10) : null;
  if (ad && bd && ad !== bd) return ad < bd ? -1 : 1;
  if (!ad && bd) return 1;
  if (ad && !bd) return -1;
  const pr = (PRIO_RANK[a.priority] ?? 99) - (PRIO_RANK[b.priority] ?? 99);
  if (pr) return pr;
  const ac = a.created_at || '';
  const bc = b.created_at || '';
  if (ac !== bc) return ac < bc ? -1 : 1;
  return (a.title || '').localeCompare(b.title || '', 'ja');
};

// ガントチャートと同じ並び順: 優先度（高い順）→ 終了日（早い順）→ 開始日（早い順）
export const ganttOrder = (getEnd, getStart) => (a, b) => {
  const pr = (PRIO_RANK[a.priority] ?? 99) - (PRIO_RANK[b.priority] ?? 99);
  if (pr) return pr;
  const ea = getEnd(a) || '9999-12-31';
  const eb = getEnd(b) || '9999-12-31';
  if (ea !== eb) return ea < eb ? -1 : 1;
  const sa = (getStart ? getStart(a) : null) || ea;
  const sb = (getStart ? getStart(b) : null) || eb;
  return sa < sb ? -1 : sa > sb ? 1 : 0;
};

export const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(String(s).slice(0, 10) + 'T00:00:00');
  if (isNaN(d)) return String(s);
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

// メンバー表示名: 基本は先頭の名前（姓/ファーストネーム）のみ。
// 同じ先頭名の人が複数いる場合（例: 鹿島 健 と 鹿島 豊）はフルネームで区別する。
export const makeNameShortener = (allNames) => {
  const first = (nm) => String(nm || '').trim().split(/[\s\u3000]+/)[0];
  const map = new Map();
  for (const nm of allNames) {
    const f = first(nm);
    if (!map.has(f)) map.set(f, new Set());
    map.get(f).add(nm);
  }
  return (nm) => ((map.get(first(nm))?.size || 0) > 1 ? nm : first(nm));
};

export const fmtDateFull = (s) => {
  if (!s) return '—';
  const d = new Date(String(s).slice(0, 10) + 'T00:00:00');
  if (isNaN(d)) return String(s);
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
};

// Group departments by division family so related units sit together
// (センスビー with センスビー, ソルダリング with ソルダリング, 技術系 together).
// Order matters: a name like センスビー技術部 matches セ first (not 技術).
export const DEPT_GROUPS = [
  { key: 'sensbey', label: 'センスビー', match: (n) => n.startsWith('センスビー') },
  { key: 'soldering', label: 'ソルダリング', match: (n) => n.startsWith('ソルダリング') },
  { key: 'heater', label: 'ヒーター', match: (n) => n.startsWith('ヒーター') },
  { key: 'gijutsu', label: '技術・研究開発', match: (n) => n.includes('技術') || n.includes('研究開発') },
  { key: 'other', label: 'その他', match: () => true },
];

export function groupDepartments(departments = []) {
  const groups = DEPT_GROUPS.map((g) => ({ ...g, items: [] }));
  for (const d of departments) {
    const name = d.name || '';
    const g = groups.find((grp) => grp.match(name)) || groups[groups.length - 1];
    g.items.push(d);
  }
  return groups.filter((g) => g.items.length > 0);
}


// U12 — 既存データの全角英数字・全角スペースは **保存データを変えずに** 表示だけ整える。
// 例: 「ＳＳ－４５ＢＭＣ　量産」→「SS－45BMC 量産」。
// 英数字と空白だけが対象（句読点や記号は日本語の文として残す）。
export const dispText = (s) => {
  if (!s) return s;
  return String(s)
    .replace(/[\uFF21-\uFF3A\uFF41-\uFF5A\uFF10-\uFF19]/g,
      (c) => String.fromCharCode(c.charCodeAt(0) - 0xFEE0))
    .replace(/\u3000/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();
};
